import { useMutation, useQuery } from '@apollo/client'
import { Autocomplete, Popover, TextField } from '@mui/material'
import { FC } from 'react'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { UpdateProcessStatus } from 'recruiter/graphql/updateProcessStatus.gql'
import { useBulkActionContext } from 'recruiter/hooks/useBulkActionContext'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem, sortProcessStatusesBySummaryGroup } from 'shared/utils'

interface EditProcessStatusProps {
    onClose: () => void
    rosterId: string
    selectedRosterCandidateIds?: string[]
    selectedCandidateIds: string[]
    isOpen: boolean
    anchorRef: React.RefObject<HTMLButtonElement>
}

const EditProcessStatus: FC<EditProcessStatusProps> = ({
    selectedRosterCandidateIds,
    selectedCandidateIds,
    rosterId,
    isOpen,
    anchorRef,
    onClose,
}) => {
    const toast = useToast()
    const { setLoading } = useBulkActionContext()
    const [updateProcessStatus] = useMutation(UpdateProcessStatus)
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    return (
        <>
            <Popover
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={onClose}
                slotProps={{
                    paper: {
                        sx: {
                            marginTop: getPxAsRem(32),
                        },
                    },
                }}
            >
                <Autocomplete
                    onChange={async (event, value) => {
                        setLoading(true)
                        const idVariables = selectedCandidateIds?.length
                            ? { rosterId, candidateIds: selectedCandidateIds }
                            : {
                                  rosterCandidateIds:
                                      selectedRosterCandidateIds,
                                  rosterId,
                              }
                        const { data } = await updateProcessStatus({
                            refetchQueries: [GetCandidatesForRosterSql],
                            variables: {
                                ...idVariables,
                                processStatus: value.status,
                            },
                        })
                        if (data?.updateCandidateProcessStatus.success) {
                            toast({
                                title: 'Success',
                                message:
                                    data?.updateCandidateProcessStatus
                                        .message ||
                                    'Candidate process status updated successfully',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data?.updateCandidateProcessStatus
                                        .message ||
                                    'Error updating process status',
                                severity: 'error',
                            })
                        }
                        onClose()
                        setLoading(false)
                    }}
                    sx={{ width: getPxAsRem(224) }}
                    options={sortProcessStatusesBySummaryGroup(
                        processStatusOptions?.processStatuses ?? []
                    )}
                    getOptionLabel={(option) => option.statusLabel}
                    isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                    }
                    groupBy={(option) =>
                        option.processStatusSummaryGroup?.label || ''
                    }
                    disableClearable
                    fullWidth
                    renderInput={(renderParams) => (
                        <TextField
                            {...renderParams}
                            fullWidth
                            placeholder="Select Process Status"
                        />
                    )}
                />
            </Popover>
        </>
    )
}

export default EditProcessStatus
